import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { UserAccount } from '../entity/UserAccount';
import { BehaviorSubject, filter, Observable, Subject, switchMap } from 'rxjs';
import { AuthService } from './auth/auth.service';
import { isPlatformBrowser } from '@angular/common';
import { Identity } from '../sdk/kratos';
import { UserAccountType } from '../entity/UserAccountType';

@Injectable({
  providedIn: 'root'
})
export class UserAccountService {
  private userSub: Subject<UserAccount> = new BehaviorSubject<UserAccount>(new UserAccount(true));

  private logoutToken!: string;

  get user(): Observable<UserAccount> {
    return this.userSub.asObservable();
  }

  constructor(private authService: AuthService,
              @Inject(PLATFORM_ID) private platformId: Object) {
    console.log('account service init');
    if (isPlatformBrowser(this.platformId)) {
      this.user.pipe(
        filter(user => user.uninitialized),
      ).subscribe(() => {
        this.checkSessionAndGetUser();
        console.log('get user successfully');
      });
      this.authService.authEvent
        .pipe(switchMap((identity: Identity | null) => {
          console.log('authEvent: ', identity);
          if (identity) {
            return this.createUserAccountAndLogoutToken(identity);
          } else {
            return this.authService.checkCurrentSession()
              .pipe(switchMap((session) => {
                if (session.identity) {
                  return this.createUserAccountAndLogoutToken(session.identity);
                } else {
                  throw new Error('NO_IDENTITY_IN_SESSION');
                }
              }))
          }
        })).subscribe({
        next: (token: string) => {
          this.logoutToken = token;
        },
        error: (err: any) => {
          if (err.message === 'NO_IDENTITY_IN_SESSION' || err.status === 401) {
            // not login
            this.userSub.next(new UserAccount());
          } else {
            console.log(err);
          }
        },
        complete: () => {
          console.log('authEvent subscribe complete');
        }
      })
    }
  }

  checkSessionAndGetUser(): void {
      this.authService.checkCurrentSession()
        .pipe(switchMap((session) => {
          if (session.identity) {
            return this.createUserAccountAndLogoutToken(session.identity);
          } else {
            throw new Error('NO_IDENTITY_IN_SESSION');
          }
        }))
        .subscribe({
          next: (token) => {
            this.logoutToken = token;
          },
          error: (err: any) => {
            if (err.message === 'NO_IDENTITY_IN_SESSION' || err.status === 401) {
              // not login
              this.userSub.next(new UserAccount());
            } else {
              console.log(err);
            }
          }
        })
  }

  logout(): void {
    if (!this.logoutToken) {
      // this is not correct, just update user
      this.userSub.next(new UserAccount());
      return;
    }
    this.authService.logout(this.logoutToken)
      .subscribe({
        next: () => {
          this.userSub.next(new UserAccount());
        },
        error: (err: any) => {
          console.log(err);
        }
      });
  }

  private createUserAccountAndLogoutToken(identity: Identity): Observable<string> {
    if (identity.verifiable_addresses?.some((addr)=> addr.verified)) {
      const userAccount = new UserAccount();
      userAccount.name = identity.traits.name;
      userAccount.kratosId = identity.id;
      const metadataPublic = identity.metadata_public;
      if (metadataPublic) {
        userAccount.type = (metadataPublic as any).role || UserAccountType.Guest;
      } else {
        userAccount.type = UserAccountType.Guest;
      }
      userAccount.verified = true;
      this.userSub.next(userAccount);
      return this.authService.createLogoutFlowToken()
    } else {
      // user email address not verified
      const userAccount = new UserAccount();
      userAccount.name = identity.traits.name;
      userAccount.kratosId = identity.id;
      userAccount.type = UserAccountType.Guest;
      this.userSub.next(userAccount);
      return this.authService.createLogoutFlowToken();
    }
  }
}
